import React from 'react';
import { useSelector } from 'react-redux';
import { Pill } from '../../../ui';
import { MonoCell, Muted } from '../../../security/security.styled';
import { PaneTable, triAlpha } from '../paneTable';
import { CellulePersonne } from '../personne';

/* ═══════════════════════════════════════════════════════════════════
   Délégations de boîte aux lettres, dans les deux sens.

   Les deux listes ne disent PAS la même chose et ne se fusionnent pas :
     · « accordées sur cette boîte » = qui peut agir sur la boîte du titulaire ;
     · « accessibles à ce compte »   = les boîtes des autres qu'il peut ouvrir.
   Un même nom peut figurer dans les deux, avec des droits différents.

   462 comptes du parc portent au moins une délégation. La collecte passe par
   deux appels Exchange par boîte et ne tourne donc qu'à 4 h : ce panneau montre
   l'état de la nuit, pas celui de la minute.
   ═══════════════════════════════════════════════════════════════════ */

/* Les trois valeurs réellement stockées, mesurées le 2026-09-10.
   « GrantSendOnBehalfTo » et non « SendOnBehalfOf » : c'est le nom que rend
   Exchange, et c'est celui qui est en base.

   Le ton suit ce qui se voit du destinataire d'un message : « en tant que »
   fait partir un courriel sous l'identité du titulaire, sans trace visible du
   délégué — c'est le droit qu'on remarque. « De la part de » l'affiche. */
const ACCES = {
    FullAccess: { libelle: 'Accès total', ton: 'accent' },              // 1 503 occurrences
    SendAs: { libelle: 'Envoyer en tant que', ton: 'warn' },            // 1 204
    GrantSendOnBehalfTo: { libelle: 'Envoyer de la part de', ton: 'neutral' }, // 342
};

const TRI_PAR_NOM = [{ id: 'displayName', desc: false }];

const COLONNES = [
    {
        header: 'Personne',
        accessorKey: 'displayName',
        sortingFn: triAlpha,
        cell: ({ row }) => <CellulePersonne personne={row.original} />,
    },
    {
        header: 'Compte',
        accessorKey: 'userPrincipalName',
        cell: ({ getValue }) => (getValue() ? <MonoCell>{getValue()}</MonoCell> : <Muted>—</Muted>),
    },
    {
        header: 'Étoile',
        accessorKey: 'department',
        cell: ({ row, getValue }) => (getValue()
            ? <>{getValue()}{row.original.companyName ? <Muted> · {row.original.companyName}</Muted> : null}</>
            : <Muted>{row.original.companyName || '—'}</Muted>),
    },
    {
        header: 'Droits',
        id: 'droits',
        enableSorting: false,
        accessorFn: (row) => (row.accessType || []).map((a) => ACCES[a]?.libelle || a).join(' '),
        cell: ({ row }) => {
            const droits = Array.isArray(row.original.accessType) ? [...new Set(row.original.accessType)] : [];
            if (droits.length === 0) return <Muted>—</Muted>;
            return (
                <span style={{ display: 'inline-flex', gap: '5px', flexWrap: 'wrap' }}>
                    {droits.map((droit) => (
                        <Pill key={droit} $tone={ACCES[droit]?.ton || 'neutral'}>
                            {ACCES[droit]?.libelle || droit}
                        </Pill>
                    ))}
                </span>
            );
        },
    },
];

function liste(source) {
    return Array.isArray(source) ? source : [];
}

export function UserDelegations() {
    const { isActiveDelegation, currentDelegation } = useSelector((state) => state.delegation);

    // Le point d'entrée renvoie { delegationOn, delegationOf } — un objet, pas
    // un tableau —, et un 204 sans corps quand le compte n'a aucune délégation.
    const donnees = isActiveDelegation ? (currentDelegation?.value ?? currentDelegation) : null;
    const surCetteBoite = liste(donnees?.delegationOn);
    const surDAutres = liste(donnees?.delegationOf);

    return (
        <>
            <PaneTable
                titre="Accès accordés sur cette boîte"
                colonnes={COLONNES}
                donnees={surCetteBoite}
                triInitial={TRI_PAR_NOM}
                placeholder="Nom, compte, étoile…"
                vide={{
                    icone: 'bx-lock-open',
                    titre: 'Aucun accès accordé',
                    texte: "Personne d'autre que le titulaire ne peut ouvrir cette boîte ni envoyer en son nom.",
                }}
            />

            <div style={{ marginTop: '22px' }}>
                <PaneTable
                    titre="Boîtes accessibles à ce compte"
                    colonnes={COLONNES}
                    donnees={surDAutres}
                    triInitial={TRI_PAR_NOM}
                    placeholder="Nom, compte, étoile…"
                    vide={{
                        icone: 'bx-mail-send',
                        titre: 'Aucune boîte déléguée',
                        texte: "Ce compte n'a de droits sur aucune autre boîte aux lettres.",
                    }}
                />
            </div>
        </>
    );
}
